import type {
  BoardCalibration,
  BoardDisabledCells,
  BoardExtraHoldMap,
  BoardHoldMap,
  BoardHoldRadii,
  BoardHoldSpec,
  BoardImageCrop,
  BoardShape,
  BoardShapes,
  CalibrationPoint,
} from '../../../types/device';
import type { BoardHold } from '../../../types/climb';
import type { BoardHoldRole } from '../../../types/device';

export const DEFAULT_CALIBRATION: Required<BoardCalibration> = {
  top_left: { x: 0.06, y: 0.06 },
  top_right: { x: 0.94, y: 0.06 },
  bottom_left: { x: 0.06, y: 0.94 },
  bottom_right: { x: 0.94, y: 0.94 },
};

/**
 * The widest a hold is drawn on a board that says nothing about its own holds —
 * the plain grids (Moonboard, SprayWall), whose cells are all one hold of one
 * size. A mapped layout sends its own radii instead, because a percentage that
 * suits a 23-cell wall is twice too big on a 49-cell one; a plain grid gets the
 * same treatment from its own row and column counts, see gridHoldWidth.
 */
export const DEFAULT_HOLD_WIDTH = 0.05;

/**
 * How much of the gap to its neighbour a hold's circle fills. Well under 1, so
 * that neighbouring cells never touch: a grid of circles that meet reads as a
 * mesh laid over the photo instead of as the holds on it, and the wall — which
 * is what the setter is actually picking holds off — disappears under it.
 */
const HOLD_PITCH_RATIO = 0.55;

/** Never draw a cell so small that it cannot be aimed at, dense wall or not. */
const MIN_HOLD_WIDTH = 0.022;

/** Cell key in a hold map, which is the same `"row,col"` the server writes. */
export function cellKey(cell: { row: number; col: number }): string {
  return `${cell.row},${cell.col}`;
}

/**
 * Identity of a grid cell, for keying React lists and for pointing at one hold
 * across the board and the circuit list. A cell holds at most one hold, so its
 * coordinates name it — an index into the holds array would move under a
 * reorder.
 */
export function holdKey(cell: { row: number; col: number }): string {
  return `${cell.row}-${cell.col}`;
}

function lerp(a: CalibrationPoint, b: CalibrationPoint, t: number): CalibrationPoint {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

/** The calibrated area's four corners, each falling back to the default frame. */
function cornersOf(calibration: BoardCalibration | null): Required<BoardCalibration> {
  return {
    top_left: calibration?.top_left ?? DEFAULT_CALIBRATION.top_left,
    top_right: calibration?.top_right ?? DEFAULT_CALIBRATION.top_right,
    bottom_left: calibration?.bottom_left ?? DEFAULT_CALIBRATION.bottom_left,
    bottom_right: calibration?.bottom_right ?? DEFAULT_CALIBRATION.bottom_right,
  };
}

/**
 * A projective map from the unit square onto the calibrated quad:
 * `(u,v) -> ((au+bv+c)/w, (du+ev+f)/w)` with `w = gu+hv+1`.
 */
interface ProjectiveMap {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
  g: number;
  h: number;
}

/** Below this, a quantity that divides is treated as having collapsed to zero. */
const DEGENERATE = 1e-9;

/**
 * How near the horizon a cell may be pushed, as a fraction of the depth the
 * near edge sits at. A real photograph of a wall stays around 1 even shot from
 * a sharp angle; anything approaching 0 is a quad folded over on itself, whose
 * far cells would be flung millions of picture-widths off screen.
 */
const MIN_DEPTH = 1e-3;

/**
 * The perspective the four corners describe, in the closed form for
 * square-to-quad (Heckbert, *Fundamentals of Texture Mapping and Image
 * Warping*, appendix A.1).
 *
 * This is what a photograph of a flat wall actually does, and the reason the
 * corners alone are enough to place every hold between them. Reading them in
 * straight lines instead — a lerp down each edge and a lerp across, which is
 * what this used to do — holds only for a photo taken dead square-on: under a
 * perspective view a grid's equal steps do not stay equal, they crowd together
 * towards the far side. Ten degrees off square already lands the middle of a
 * 25-column wall about eight tenths of a hold out, and no placing of the four
 * handles can recover it, because the shape of the error is not one the
 * straight-line reading can take. Hence a setter who nudges the corners until
 * the centre lines up and finds the edges have gone.
 *
 * Null when the corners no longer describe a view of a flat wall at all — see
 * cellPosition, which still has to draw something.
 */
function projectiveMap(corners: Required<BoardCalibration>): ProjectiveMap | null {
  const { top_left: p0, top_right: p1, bottom_right: p2, bottom_left: p3 } = corners;

  const sx = p0.x - p1.x + p2.x - p3.x;
  const sy = p0.y - p1.y + p2.y - p3.y;

  // Opposite edges still parallel: the wall was shot square-on, there is no
  // vanishing point to bend towards, and the map is the plain affine one. Taken
  // early because the general form below divides one vanishing term by another.
  if (Math.abs(sx) < DEGENERATE && Math.abs(sy) < DEGENERATE) {
    return {
      a: p1.x - p0.x,
      b: p3.x - p0.x,
      c: p0.x,
      d: p1.y - p0.y,
      e: p3.y - p0.y,
      f: p0.y,
      g: 0,
      h: 0,
    };
  }

  const dx1 = p1.x - p2.x;
  const dx2 = p3.x - p2.x;
  const dy1 = p1.y - p2.y;
  const dy2 = p3.y - p2.y;
  const den = dx1 * dy2 - dx2 * dy1;

  // The two edges meeting at the bottom-right corner run along one line: the
  // quad has been dragged flat and encloses no area to map a grid onto.
  if (Math.abs(den) < DEGENERATE) return null;

  const g = (sx * dy2 - dx2 * sy) / den;
  const h = (dx1 * sy - sx * dy1) / den;

  // The denominator is affine in (u, v), so across the square it stays on the
  // side of zero it starts on — which at the top-left corner is 1 — exactly
  // when it does so at all four corners. Where it does not, some cell between
  // them crosses the horizon and is thrown to infinity; dragging one handle
  // past its neighbour is enough to do it.
  if (1 + g < MIN_DEPTH || 1 + h < MIN_DEPTH || 1 + g + h < MIN_DEPTH) return null;

  return {
    a: p1.x - p0.x + g * p1.x,
    b: p3.x - p0.x + h * p3.x,
    c: p0.x,
    d: p1.y - p0.y + g * p1.y,
    e: p3.y - p0.y + h * p3.y,
    f: p0.y,
    g,
    h,
  };
}

/**
 * The row an extra hold is addressed by — one screwed on between the t-nuts,
 * where the grid has no cell for it. Negative, so it cannot collide with a grid
 * cell however the wall is re-counted. Kept in step with Board::EXTRA_HOLD_ROW,
 * which is where the same rule decides that such a hold lights no LED.
 */
export const EXTRA_HOLD_ROW = -1;

/** Whether a cell addresses an extra hold rather than a place on the grid. */
export function isExtraHold(row: number): boolean {
  return row === EXTRA_HOLD_ROW;
}

/**
 * The row a hold found by the scan of the board's photo is addressed by, its
 * column being the hold's place in the scan (docs/SPRAY_WALL_SCAN_PLAN.md).
 * Kept in step with Board::SCANNED_HOLD_ROW.
 */
export const SCANNED_HOLD_ROW = -2;

/** Whether a cell addresses a hold the scan of the board's photo found. */
export function isScannedHold(row: number): boolean {
  return row === SCANNED_HOLD_ROW;
}

/**
 * **A wall described by its photo's scan**, as it travels: every hold's
 * outline by place, on the 0-1000 grid of the photo *as uploaded*; the window
 * of that photo the picture drawn is (`image_crop`); and the places a setter
 * hid — offered to nobody, still drawn on a problem that uses them.
 */
export interface ScannedWall {
  outlines: Record<string, Array<[number, number]>>;
  crop?: BoardImageCrop | Record<string, never> | null;
  hidden?: number[] | null;
}

/** Whether a board is described by its scan: it has any hold outline at all. */
export function isScannedWall(wall: ScannedWall | null | undefined): wall is ScannedWall {
  return !!wall && Object.keys(wall.outlines).length > 0;
}

/** The scanned wall a board's payload describes, or null. */
export function scannedWallOf(
  outlines: Record<string, Array<[number, number]>> | null | undefined,
  crop?: BoardImageCrop | Record<string, never> | null,
  hidden?: number[] | null
): ScannedWall | null {
  return outlines && Object.keys(outlines).length > 0 ? { outlines, crop, hidden } : null;
}

/**
 * A scanned hold's outline **on the picture drawn**: the scan measured it on
 * the upload, and the picture is a window cut out of that (`crop`). Null for a
 * place the scan has no outline for.
 */
export function scannedOutline(wall: ScannedWall | null | undefined, place: number): CalibrationPoint[] | null {
  const outline = wall?.outlines[String(place)];
  if (!outline || outline.length < 3) return null;

  const crop = cropOf(wall?.crop);
  return outline.map(([x, y]) => ({
    x: (x / 1000 - crop.x) / crop.width,
    y: (y / 1000 - crop.y) / crop.height,
  }));
}

/**
 * Where a traced hold is pressed and how wide it is, as fractions of the
 * picture: the middle of its box and the box's width. What the round target
 * laid over an outline is sized from.
 */
export function outlineFrame(outline: CalibrationPoint[]): { centre: CalibrationPoint; width: number } {
  const xs = outline.map((point) => point.x);
  const ys = outline.map((point) => point.y);
  const [minX, maxX, minY, maxY] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];

  return { centre: { x: (minX + maxX) / 2, y: (minY + maxY) / 2 }, width: maxX - minX };
}

/**
 * Where a point `(u, v)` of the calibrated quad falls in the picture (0-1),
 * with `(0,0)` the top-left corner and `(1,1)` the bottom-right.
 *
 * Takes any point rather than only the grid's own, because the holds that are
 * not on the grid are placed by exactly this: a screw-on two thirds across and
 * a third of the way down is `(0.66, 0.33)`, and it has to land where the
 * perspective puts it just as a grid cell does.
 */
export function quadPosition(
  calibration: BoardCalibration | null,
  u: number,
  v: number
): CalibrationPoint {
  const corners = cornersOf(calibration);
  const map = projectiveMap(corners);

  // Handles dragged into a shape no photograph could have produced. The
  // straight-line reading is wrong there too, but it is bounded and it keeps
  // following the handles, which is what someone needs while dragging back out
  // of the tangle they have just made.
  if (!map) {
    const top = lerp(corners.top_left, corners.top_right, u);
    const bottom = lerp(corners.bottom_left, corners.bottom_right, u);
    return lerp(top, bottom, v);
  }

  const w = map.g * u + map.h * v + 1;

  return {
    x: (map.a * u + map.b * v + map.c) / w,
    y: (map.d * u + map.e * v + map.f) / w,
  };
}

/**
 * The reverse of quadPosition: which point of the calibrated quad a place in
 * the picture is. What turns a tap on the photo into a hold's position, so a
 * screw-on is put where it was clicked and stays there when the corners move.
 *
 * A projective map inverts to another one, so this is the adjugate of the
 * forward matrix — the determinant is left out because homogeneous coordinates
 * are only defined up to scale and the division by `w` takes it out again.
 *
 * Null where the corners describe no usable perspective (see projectiveMap), or
 * where the tap lands on the far side of the horizon and belongs to no part of
 * the wall at all.
 */
export function quadCoordinates(
  calibration: BoardCalibration | null,
  x: number,
  y: number
): { u: number; v: number } | null {
  const m = projectiveMap(cornersOf(calibration));
  if (!m) return null;

  const { a, b, c, d, e, f, g, h } = m;
  const uw = (e - f * h) * x + (c * h - b) * y + (b * f - c * e);
  const vw = (f * g - d) * x + (a - c * g) * y + (c * d - a * f);
  const w = (d * h - e * g) * x + (b * g - a * h) * y + (a * e - b * d);

  if (!Number.isFinite(w) || Math.abs(w) < DEGENERATE) return null;

  const u = uw / w;
  const v = vw / w;

  return Number.isFinite(u) && Number.isFinite(v) ? { u, v } : null;
}

/**
 * How far outside the calibrated grid an extra hold may be placed, in grids: 1
 * is a whole wall's width past each edge.
 *
 * The holds the grid cannot reach are not all *between* its cells. A volume
 * bolted beside the wall, a strip of panel the corners were not dragged over, a
 * hold above the last row — those sit outside the quad, and a hold there is
 * still a hold a setter puts in a problem, so the quad cannot be the boundary.
 * What remains is a bound rather than a boundary: outside the quad the
 * perspective divides by a smaller and smaller depth, so a position far out
 * swings across the picture as soon as a corner is nudged, and no wall carries a
 * volume a whole wall's width away from it. Kept in step with
 * Board::EXTRA_HOLD_RANGE, which is where a stored position is held to it.
 */
export const EXTRA_HOLD_MARGIN = 1;

/**
 * Where a tap on the picture places an extra hold, as a point of the calibrated
 * quad — `null` where it places none.
 *
 * That is: the corners in a shape no perspective can be read out of (see
 * quadCoordinates), a tap so far outside the grid that it cannot be pointing at
 * this wall, and a tap on the far side of the wall's own horizon — the part of
 * the picture where the wall's plane, carried on past the edge of the wall,
 * comes back as sky. A point there does have quad coordinates, and it would even
 * draw under the finger, but it is behind the camera: it belongs to the wall
 * only in the way the vanishing point does.
 */
export function extraHoldSpot(
  calibration: BoardCalibration | null,
  x: number,
  y: number
): { u: number; v: number } | null {
  const spot = quadCoordinates(calibration, x, y);
  if (!spot) return null;

  const { u, v } = spot;
  const reach = 0.5 + EXTRA_HOLD_MARGIN;
  if (Math.abs(u - 0.5) > reach || Math.abs(v - 0.5) > reach) return null;

  // Non-null: quadCoordinates has already answered null for the corners that
  // describe no map at all.
  const map = projectiveMap(cornersOf(calibration));
  if (map && map.g * u + map.h * v + 1 < MIN_DEPTH) return null;

  return spot;
}

/**
 * The width every hold of a wall drawn as lines is given, as a fraction of the
 * picture.
 *
 * One size for the lot, rather than worked out from how close together they
 * sit. A grid has a pitch to read a size off (see gridHoldWidth); a drawing has
 * none — two lines may be a hand apart on one panel and a metre apart on the
 * next, and a size that followed each line would draw one wall's holds at four
 * different scales. Deliberately on the small side: these walls are the dense
 * ones, and a circle that covers the hole beside the one being aimed at is
 * worse than one a little smaller than the hold it marks.
 */
export const SHAPE_HOLD_WIDTH = 0.025;

/** One hold of a wall drawn as lines: the cell naming it, and where it sits. */
export interface ShapeHold {
  /** Which line it is on — the cell's row, so `"row,col"` keys keep working. */
  row: number;
  /** How far along that line, counted from the end it was drawn from. */
  col: number;
  u: number;
  v: number;
  led: number;
}

/** Whether this wall is drawn as lines rather than ruled as a grid. */
export function isShapedWall(shapes: BoardShapes | null | undefined): shapes is BoardShapes {
  return !!shapes && shapes.length > 0;
}

/** Below this a drawing has no extent to speak of in one direction. */
const FLAT = 1e-6;

/**
 * The box the lines sit in, in the quad's own coordinates — null for a wall
 * with no lines on it.
 */
export function shapeBounds(
  shapes: BoardShapes | null | undefined
): { u0: number; v0: number; u1: number; v1: number } | null {
  if (!isShapedWall(shapes)) return null;

  const us = shapes.flatMap((shape) => [shape.from.u, shape.to.u]);
  const vs = shapes.flatMap((shape) => [shape.from.v, shape.to.v]);

  return { u0: Math.min(...us), v0: Math.min(...vs), u1: Math.max(...us), v1: Math.max(...vs) };
}

/**
 * The drawing read into another quad, which is how a drawn wall's four corners
 * come to be in two places depending on what is being done to them.
 *
 * A drawing is stored in the quad's coordinates rather than the picture's, and
 * that is what makes dragging a corner move the whole drawing as one body. But
 * the same thing makes the quad a cage while the wall is being drawn: the reach
 * a point may be placed within is measured in quads (EXTRA_HOLD_MARGIN, and
 * Board::SHAPE_POINT_RANGE behind it), so a quad fitted around the first two
 * lines of a drawing left "one quad out" at a couple of percent of the wall —
 * and a click past that was dropped in silence. Hence two framings and a move
 * between them, rather than one framing that has to be both:
 *
 * - **pinShapesToFrame**, while the wall is being drawn on: the quad on the
 *   picture, so one quad out is one picture out and any part of the photo can be
 *   drawn on.
 * - **frameShapesToDrawing**, when the corners are taken in hand: the quad
 *   around the lines, so the handles are on the wall and dragging one means
 *   something.
 *
 * **Nothing moves on the photo either way**, and that takes two steps rather
 * than one. Each end is read into the new frame, so it keeps its pixel. The
 * holds *between* two ends do not come along for free: they are spread along the
 * line in the frame it is stored in, and an even step in a quad carrying a
 * perspective is not an even step in the picture. So whatever the old frame was
 * saying about each line's spacing is solved back out of it and handed to the
 * line itself, as its taper (see carriedTaper). The perspective is not dropped,
 * it changes hands.
 *
 * Null when there is nothing safe to do: a point the new frame would put further
 * out than one may be stored. Better a frame left where it is than a save that
 * clamps the drawing onto different holes.
 */
function movedShapes(
  shapes: BoardShapes,
  extraHolds: BoardExtraHoldMap | null | undefined,
  corners: Required<BoardCalibration>,
  place: (point: { u: number; v: number }) => { u: number; v: number }
): { calibration: BoardCalibration; shapes: BoardShapes; extraHolds: BoardExtraHoldMap } | null {
  const moved = shapes.map((shape) => {
    const ends = { ...shape, from: place(shape.from), to: place(shape.to) };

    return { ...ends, taper: carriedTaper(shape, ends, place) };
  });
  const placed = Object.entries(extraHolds ?? {}).map(([id, hold]) => [id, place(hold)] as const);
  const points = [
    ...moved.flatMap((shape) => [shape.from, shape.to]),
    ...placed.map(([, hold]) => hold),
  ];
  if (!points.every(inGridReach)) return null;

  return { calibration: corners, shapes: moved, extraHolds: Object.fromEntries(placed) };
}

/**
 * The drawing with the quad **nailed to the picture the wall is drawn on** —
 * the framing a wall is drawn in. See movedShapes for why there are two.
 *
 * Null when there is nothing to do (no lines, the quad already on the picture)
 * as well as when there is nothing safe to do.
 */
export function pinShapesToFrame(
  calibration: BoardCalibration | null,
  shapes: BoardShapes | null | undefined,
  extraHolds: BoardExtraHoldMap | null | undefined,
  frame: BoardImageCrop
): { calibration: BoardCalibration; shapes: BoardShapes; extraHolds: BoardExtraHoldMap } | null {
  if (!isShapedWall(shapes)) return null;

  const corners = frameCorners(frame);
  if (samePlace(cornersOf(calibration), corners)) return null;

  return movedShapes(shapes, extraHolds, corners, (point) => {
    const at = croppedPoint(quadPosition(calibration, point.u, point.v), frame);

    return { u: at.x, v: at.y };
  });
}

/**
 * The drawing with the four corners put **around the lines** — the framing the
 * corners are adjusted in, and what taking them in hand builds.
 *
 * The handles are what a wall is placed and aligned with, and they can only mean
 * that if they are on the wall: a quad on the frame of the photo while the lines
 * were drawn in the middle of it is a box around nothing, and dragging one of
 * its corners pulls the drawing by an arm's length it does not have. So the box
 * the lines fill is what the corners are given to hold, and the lines are
 * renumbered inside it.
 *
 * Null when there is nothing to do — no lines, the corners already round them —
 * or nothing safe to do: a drawing flat in one direction (a single row has no
 * height to frame, and dividing by it would throw the drawing off the picture),
 * or a screw-on that the tighter frame would push out of reach.
 */
export function frameShapesToDrawing(
  calibration: BoardCalibration | null,
  shapes: BoardShapes | null | undefined,
  extraHolds?: BoardExtraHoldMap | null
): { calibration: BoardCalibration; shapes: BoardShapes; extraHolds: BoardExtraHoldMap } | null {
  const bounds = shapeBounds(shapes);
  if (!bounds || !shapes) return null;

  const { u0, v0, u1, v1 } = bounds;
  const width = u1 - u0;
  const height = v1 - v0;
  if (width < FLAT || height < FLAT) return null;
  if (near(u0, 0) && near(v0, 0) && near(width, 1) && near(height, 1)) return null;

  const corners = {
    top_left: quadPosition(calibration, u0, v0),
    top_right: quadPosition(calibration, u1, v0),
    bottom_left: quadPosition(calibration, u0, v1),
    bottom_right: quadPosition(calibration, u1, v1),
  };
  // A corner further off the photo than one may be stored (CALIBRATION_RANGE).
  if (!Object.values(corners).every(withinReach)) return null;

  return movedShapes(shapes, extraHolds, corners, (point) => ({
    u: (point.u - u0) / width,
    v: (point.v - v0) / height,
  }));
}

/** Whether a corner is somewhere a calibration may be stored (Board::CALIBRATION_RANGE). */
function withinReach(point: CalibrationPoint): boolean {
  const reach = (value: number) => Number.isFinite(value) && value >= -1 && value <= 2;

  return reach(point.x) && reach(point.y);
}

/**
 * The taper that keeps a line's holds where the quad it is leaving had them.
 *
 * One hold settles it, so this asks the old frame where one of them was and
 * solves for that — the middle one, because it is the furthest from both ends
 * and so the least troubled by the float of two perspectives. Every other hold
 * follows exactly: both readings are projective along the same segment, and a
 * projective map of it keeping both ends has one degree of freedom.
 *
 * The line's own taper is already in what the old frame answers (see spotAlong),
 * so a line that had been corrected by hand keeps its correction.
 */
function carriedTaper(
  shape: BoardShape,
  ends: BoardShape,
  place: (point: { u: number; v: number }) => { u: number; v: number }
): number | null {
  const count = holdCount(shape);
  // Two holds are the two ends, and those were just put where they belong.
  if (count < 3) return null;

  const col = Math.floor((count - 1) / 2);
  const carried = taperFor(ends, col, alongLine(ends, place(spotAlong(shape, col))));

  // A line that comes out evenly spread says so by saying nothing, the one form
  // the server keeps (Board#normalized_taper). A frame and back is exact on
  // paper and a few parts in a quadrillion in a double, and that much taper
  // moves a hold by less than a wavelength of light: snapped, so repeated
  // framings cannot accumulate a number out of nothing.
  return carried !== null && near(carried, EVEN_TAPER) ? null : carried;
}

/** A quad laid exactly on a window of the picture, corner for corner. */
function frameCorners(frame: BoardImageCrop): Required<BoardCalibration> {
  const right = frame.x + frame.width;
  const bottom = frame.y + frame.height;

  return {
    top_left: { x: frame.x, y: frame.y },
    top_right: { x: right, y: frame.y },
    bottom_left: { x: frame.x, y: bottom },
    bottom_right: { x: right, y: bottom },
  };
}

/** Whether two quads are the same one, give or take the float of a conversion. */
function samePlace(a: Required<BoardCalibration>, b: Required<BoardCalibration>): boolean {
  const corners: Array<keyof Required<BoardCalibration>> = [
    'top_left',
    'top_right',
    'bottom_left',
    'bottom_right',
  ];

  return corners.every(
    (corner) => near(a[corner].x, b[corner].x) && near(a[corner].y, b[corner].y)
  );
}

function near(value: number, to: number): boolean {
  return Math.abs(value - to) < FLAT;
}

/** The bound on a hold's place in the quad — Board::EXTRA_HOLD_RANGE. */
function inGridReach(hold: { u: number; v: number }): boolean {
  const reach = 0.5 + EXTRA_HOLD_MARGIN;

  return Math.abs(hold.u - 0.5) <= reach && Math.abs(hold.v - 0.5) <= reach;
}

/**
 * Where each line's LEDs start, with the ones nobody set worked out from the
 * line before: the strip is stapled across the wall in one run and numbered
 * from one end, so a line carries on from where the last one stopped. A line
 * with no holds is only a path between two panels — it consumes no LED, so the
 * line after it starts where it would have anyway.
 *
 * Kept in step with Board#shape_led_starts, which is the same rule where it is
 * authoritative. Worked out here as well because the editor renumbers as the
 * drawing is edited, before any of it has been saved.
 */
export function shapeLedStarts(shapes: BoardShapes | null | undefined): number[] {
  let next = 0;

  return (shapes ?? []).map((shape) => {
    const start = shape.led_start ?? next;
    next = start + holdCount(shape);
    return start;
  });
}

/**
 * Every hold the lines carry, in the order the strip runs: which line it is on,
 * how far along it, where it sits inside the quad and which LED lights it.
 *
 * The holds run from one end of a line to the other, **both ends included** — a
 * row of ten is the two holes at the ends and eight between them, which is how
 * a line is drawn: from the first hold to the last. A line with a single hold
 * puts it where the line starts, there being no span to spread anything over.
 *
 * Evenly, unless the line says otherwise. A run of holes photographed at an
 * angle is not evenly spaced on the photo, and what says how it is spaced is
 * the line's own `taper` rather than the wall's four corners — see taperOf for
 * why the perspective is a line's business on a wall that was drawn.
 */
export function shapeHolds(shapes: BoardShapes | null | undefined): ShapeHold[] {
  const starts = shapeLedStarts(shapes);

  return (shapes ?? []).flatMap((shape, row) => {
    const count = holdCount(shape);

    return Array.from({ length: count }, (_, col) => ({
      row,
      col,
      ...spotAlong(shape, col),
      led: starts[row] + col,
    }));
  });
}

/**
 * One hold of one line, as a point of the quad — null for a hold no line has.
 * That is what a problem set before the line was shortened points at: the holds
 * are named by where they sit, so a line that lost its end lost them with it.
 */
export function shapeHoldSpot(
  shapes: BoardShapes | null | undefined,
  row: number,
  col: number
): { u: number; v: number } | null {
  const shape = shapes?.[row];
  if (!shape || row < 0 || col < 0 || col >= holdCount(shape)) return null;

  return spotAlong(shape, col);
}

/** How far along a line its nth hold sits, both ends of it included. */
function spotAlong(shape: BoardShape, col: number): { u: number; v: number } {
  const count = holdCount(shape);
  const even = count > 1 ? col / (count - 1) : 0;
  const t = taperedAlong(even, taperOf(shape));

  return {
    u: shape.from.u + (shape.to.u - shape.from.u) * t,
    v: shape.from.v + (shape.to.v - shape.from.v) * t,
  };
}

/**
 * A line whose holds are spread evenly along it. What a line that says nothing
 * about its own spacing is drawn with.
 */
export const EVEN_TAPER = 1;

/**
 * How far from even a line's holds may be bunched towards one of its ends.
 *
 * A bound rather than a limit anyone should meet. Past it a run is no longer a
 * photograph of a straight row of holes but a hold dragged onto the end of its
 * own line, where the solution runs off to nothing and to infinity and every
 * other hold piles up in one place. Kept in step with Board::SHAPE_TAPER_RANGE.
 */
export const SHAPE_TAPER_RANGE = { min: 0.05, max: 20 };

/**
 * How a line's holds are bunched along it: 1 spreads them evenly, below 1
 * crowds them towards the end the line was drawn **to**, above 1 towards the
 * end it was drawn **from**.
 *
 * This is the line's own perspective, and it is the one shape the bunching can
 * take rather than a curve picked for the feel of it. A straight run of holes
 * on a flat wall, photographed, is spaced along its image by a projective
 * reparametrization — equal steps on the wall, a cross-ratio on the photo — and
 * holding both ends put leaves exactly one number of that free. This is it.
 *
 * It is what the four corners already do for a grid, said per line instead. A
 * drawn wall is a spray wall and a spray wall is not one plane: a volume, a
 * dihedral, the panel that turns the corner. One quad describes one plane, so
 * no placing of four handles can space every line of such a wall — and on a
 * wall with no rectangle anywhere on it, placing them is guesswork with nothing
 * to check it against. A taper is set by dragging one hold onto the hole it
 * belongs to, and then the photo itself shows whether it is right.
 */
function taperOf(shape: BoardShape): number {
  const taper = shape.taper;
  if (typeof taper !== 'number' || !Number.isFinite(taper) || taper <= 0) return EVEN_TAPER;

  return clamp(taper, SHAPE_TAPER_RANGE.min, SHAPE_TAPER_RANGE.max);
}

/**
 * Where the hold `even` of the way along a line actually sits on it:
 * `t = s / (s + k(1 - s))`, which is the projective reparametrization of a
 * segment that keeps both of its ends — so no taper ever moves the first or the
 * last hold off the hole it was clicked onto.
 */
function taperedAlong(even: number, taper: number): number {
  const spread = even + taper * (1 - even);

  return spread <= 0 ? even : even / spread;
}

/**
 * How far along a line a point of the quad falls — 0 at the end it was drawn
 * from, 1 at the other, and past either for a point beyond them.
 *
 * The point is dropped onto the line rather than asked to be on it: this is
 * what a pointer dragging a hold hands over, and a hand following a line across
 * a photograph is never exactly on it.
 */
export function alongLine(shape: BoardShape, spot: { u: number; v: number }): number {
  const du = shape.to.u - shape.from.u;
  const dv = shape.to.v - shape.from.v;
  const length = du * du + dv * dv;
  // A line dragged onto its own start has no direction to measure along.
  if (length < FLAT * FLAT) return 0;

  return ((spot.u - shape.from.u) * du + (spot.v - shape.from.v) * dv) / length;
}

/** How near an end a hold may be dragged before the taper it implies is capped. */
const TAPER_EDGE = 1e-3;

/**
 * The taper that puts hold `col` of a line `t` of the way along it — the number
 * a hold dragged onto its real hole leaves behind.
 *
 * One hold is all it takes, and that is the point of the gesture: the ends are
 * already on the first and last hole, a projective map of a segment keeping both
 * ends has one degree of freedom, and one hold spends it. So every other hold
 * on the line moves to where it belongs as this one is dragged, and the setter
 * is reading the answer off the photo rather than typing a number at it.
 *
 * Null where there is nothing to solve: a line of two holds has nothing between
 * its ends, and an end is not a handle — those are moved by moving the line.
 */
export function taperFor(shape: BoardShape, col: number, t: number): number | null {
  const count = holdCount(shape);
  if (count < 3 || col <= 0 || col >= count - 1) return null;

  const even = col / (count - 1);
  // Held short of either end, where the solution runs off to nothing and to
  // infinity: a hold dragged onto the end of its own line says nothing about
  // where the rest of them go.
  const at = clamp(t, TAPER_EDGE, 1 - TAPER_EDGE);

  return clamp(
    (even * (1 - at)) / (at * (1 - even)),
    SHAPE_TAPER_RANGE.min,
    SHAPE_TAPER_RANGE.max
  );
}

/** A line's holds, as a whole number of them and never fewer than none. */
function holdCount(shape: BoardShape): number {
  return Number.isFinite(shape.holds) ? Math.max(0, Math.trunc(shape.holds)) : 0;
}

/**
 * Where grid cell (row, col) falls in the picture (0-1), read out of the four
 * calibrated corners as the perspective they describe. Exact for any camera
 * angle, so the corners can be put on the corner holds and left there.
 */
export function cellPosition(
  calibration: BoardCalibration | null,
  rows: number,
  columns: number,
  row: number,
  col: number
): CalibrationPoint {
  // A lone row or column has no span to walk along, so it sits down the middle.
  const u = columns > 1 ? col / (columns - 1) : 0.5;
  const v = rows > 1 ? row / (rows - 1) : 0.5;

  return quadPosition(calibration, u, v);
}

/**
 * Where any of this wall's holds falls in the picture, on the grid or off it.
 *
 * An extra hold carries its own place in the quad — the board stores it once,
 * under the id the cell names — so it is looked up rather than worked out. One
 * whose id the wall no longer carries has been unscrewed since the problem was
 * set: it is put in the middle of the quad rather than nowhere, so a setter can
 * see there is a hold to deal with instead of losing it silently.
 *
 * A wall drawn as lines is read the same way, and for the same reason: there
 * the row says which line the hold is on, which means nothing without the
 * lines. A hold whose line has since been shortened or deleted gets the same
 * treatment as an unscrewed screw-on.
 */
export function holdPosition(
  calibration: BoardCalibration | null,
  rows: number,
  columns: number,
  extraHolds: BoardExtraHoldMap | null | undefined,
  row: number,
  col: number,
  shapes?: BoardShapes | null,
  scanned?: ScannedWall | null
): CalibrationPoint {
  // A traced hold sits where its outline is; one the scan no longer has goes
  // where an unscrewed screw-on does.
  if (isScannedHold(row)) {
    const outline = scannedOutline(scanned, col);
    return outline ? outlineFrame(outline).centre : { x: 0.5, y: 0.5 };
  }

  if (!isExtraHold(row)) {
    if (!isShapedWall(shapes)) return cellPosition(calibration, rows, columns, row, col);

    const spot = shapeHoldSpot(shapes, row, col);

    return quadPosition(calibration, spot?.u ?? 0.5, spot?.v ?? 0.5);
  }

  const extra = extraHolds?.[String(col)];

  return quadPosition(calibration, extra?.u ?? 0.5, extra?.v ?? 0.5);
}

/**
 * How far apart two neighbouring cells sit, as a fraction of the picture's
 * *width* — which is the unit a hold is measured in, its circle being laid out
 * from a width and then squared off by its aspect ratio.
 *
 * A hold has to fit the tighter of the two gaps, and which one that is depends
 * on the wall: a Moonboard is 18 rows of 11 holds, so its rows crowd it, while
 * a wide spray wall runs out of room across. `aspect` — the picture's width
 * over its height — is what converts the gap between two rows out of fractions
 * of the height; before the photo has loaded there is no aspect to divide by
 * and only the gap across is known.
 *
 * Both edges of the calibrated area are averaged, so a wall photographed a
 * little off-square still yields one number per direction.
 */
export function cellPitch(
  calibration: BoardCalibration | null,
  rows: number,
  columns: number,
  aspect: number | null
): number {
  const { top_left: tl, top_right: tr, bottom_left: bl, bottom_right: br } = cornersOf(calibration);
  // A single row or column has no neighbour to keep its distance from, so that
  // direction leaves the hold all the room it wants.
  const across =
    columns > 1
      ? (Math.abs(tr.x - tl.x) + Math.abs(br.x - bl.x)) / 2 / (columns - 1)
      : Number.POSITIVE_INFINITY;
  const down =
    rows > 1 && aspect
      ? (Math.abs(bl.y - tl.y) + Math.abs(br.y - tr.y)) / 2 / (rows - 1) / aspect
      : Number.POSITIVE_INFINITY;

  return Math.min(across, down);
}

/**
 * How wide to draw a cell of a plain grid, whose holds the board does not
 * describe. It is the grid itself that says how big they can be: 12 x 12 leaves
 * a cell four times the room 25 x 25 does, and one fixed percentage across both
 * either wastes the photo on a sparse wall or buries it under overlapping
 * circles on a dense one. Bounded at both ends — never wider than a hold on the
 * sparsest wall, never too small to tap on the densest.
 */
export function gridHoldWidth(
  calibration: BoardCalibration | null,
  rows: number,
  columns: number,
  aspect: number | null
): number {
  const room = cellPitch(calibration, rows, columns, aspect) * HOLD_PITCH_RATIO;

  return Math.min(DEFAULT_HOLD_WIDTH, Math.max(MIN_HOLD_WIDTH, room));
}

/**
 * How wide to draw this wall's holds, whichever kind of wall it is: the room
 * its grid leaves, or the one size a drawn wall gives them all — see
 * SHAPE_HOLD_WIDTH for why a drawing has no pitch to read a size off.
 */
export function wallHoldWidth(
  calibration: BoardCalibration | null,
  rows: number,
  columns: number,
  aspect: number | null,
  shapes?: BoardShapes | null
): number {
  if (isShapedWall(shapes)) return SHAPE_HOLD_WIDTH;

  return gridHoldWidth(calibration, rows, columns, aspect);
}

/**
 * The cells a setter may tap, in reading order.
 *
 * A mapped layout is sparse — a Kilter's lattice is mostly the gaps between its
 * holds, and only a third of its cells carry one — so the wall is its hold map
 * rather than every cell of the grid. A board without one is a plain grid where
 * every cell is a hold, which is what the Moonboards and spray walls are.
 *
 * `disabledCells` takes back the cells the gym says carry nothing: a t-nut
 * behind a volume, a panel nobody drilled, the corner a rectangular grid claims
 * on a wall that is not one. Left out here alone — a problem already set on such
 * a cell still draws it and the wall still lights it, since an LED's position
 * comes from the row and column rather than from this list.
 *
 * A wall drawn as lines has neither a grid nor a hold map: its holds are the
 * ones strung along its lines, in the order the strip runs, and they win over
 * both. Such a wall is never a mapped layout — the two are refused together.
 */
export function boardCells(
  holds: BoardHoldMap | null | undefined,
  rows: number,
  columns: number,
  extraHolds?: BoardExtraHoldMap | null,
  disabledCells?: BoardDisabledCells | null,
  shapes?: BoardShapes | null,
  scanned?: ScannedWall | null
): Array<{ row: number; col: number; hold: BoardHoldSpec | null }> {
  // **A wall described by its scan** is its scanned holds, the hidden ones
  // left out — they are offered to nobody — and wins over everything else.
  if (isScannedWall(scanned)) {
    const hidden = new Set(scanned.hidden ?? []);
    return Object.keys(scanned.outlines)
      .map(Number)
      .filter((place) => Number.isInteger(place) && !hidden.has(place))
      .sort((a, b) => a - b)
      .map((place) => ({ row: SCANNED_HOLD_ROW, col: place, hold: null }));
  }

  const off = disabledCellSet(disabledCells);
  const kept = <T extends { row: number; col: number }>(cells: T[]) =>
    off.size === 0 ? cells : cells.filter((cell) => !off.has(cellKey(cell)));

  // The screw-ons come after the wall itself, so they are the last things
  // tabbed onto rather than sitting between two grid cells in reading order.
  const extras = Object.keys(extraHolds ?? {})
    .map(Number)
    .filter((id) => Number.isInteger(id) && id >= 0)
    .sort((a, b) => a - b)
    .map((id) => ({ row: EXTRA_HOLD_ROW, col: id, hold: null }));

  if (isShapedWall(shapes)) {
    const drawn = shapeHolds(shapes).map(({ row, col }) => ({ row, col, hold: null }));

    return [...kept(drawn), ...extras];
  }

  if (holds && Object.keys(holds).length > 0) {
    const mapped = Object.entries(holds)
      .map(([key, hold]) => {
        const [row, col] = key.split(',').map(Number);
        return { row, col, hold };
      })
      .filter(({ row, col }) => Number.isInteger(row) && Number.isInteger(col))
      .sort((a, b) => a.row - b.row || a.col - b.col);

    return [...kept(mapped), ...extras];
  }

  const cells = [];
  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < columns; col += 1) cells.push({ row, col, hold: null });
  }
  return [...kept(cells), ...extras];
}

/** The disabled cells as a set of `"row,col"` keys, for asking about one. */
export function disabledCellSet(disabledCells: BoardDisabledCells | null | undefined): Set<string> {
  return new Set(disabledCells ?? []);
}

/**
 * How wide to draw a hold, as a fraction of the picture. A mapped layout states
 * one size per kind, which is what puts the small circles on the screw-on foot
 * chips and the large ones on the bolt-ons; a hold it says nothing about takes
 * the one size the plain grid it belongs to has room for (see gridHoldWidth).
 */
export function holdWidth(
  hold: BoardHoldSpec | null | undefined,
  radii: BoardHoldRadii | null | undefined,
  fallback: number = DEFAULT_HOLD_WIDTH
): number {
  const radius = hold && radii ? radii[hold.kind] : undefined;

  // Stored as a radius, drawn as a width.
  return radius ? radius * 2 : fallback;
}

/**
 * Whether a hold is too small to pull on: a screw-on chip, or a bolt-on the
 * layout marks foot-only. A cell on a plain grid never is.
 */
export function isFootOnly(hold: BoardHoldSpec | null | undefined): boolean {
  return !!hold && (hold.kind === 'foot' || hold.foot_only === true);
}

/**
 * The role a tap actually paints, given the colour selected in the legend.
 *
 * A hold that can only be stood on comes out a foot whatever was selected — the
 * setter pointed at a real hold, and there is only one thing that hold can be,
 * so correcting the tap serves them better than refusing it. Kept in step with
 * BoardType.role_for, which applies the same rule when the climb is saved.
 */
export function roleForHold(
  hold: BoardHoldSpec | null | undefined,
  role: BoardHoldRole
): BoardHoldRole {
  return isFootOnly(hold) ? 'foot' : role;
}

/** The whole picture: what a board nobody has cropped is drawn through. */
export const FULL_IMAGE_CROP: BoardImageCrop = { x: 0, y: 0, width: 1, height: 1 };

/**
 * The smallest window a crop may leave, as a fraction of the picture. A wall is
 * never a hundredth of the photo it was shot in, and a window this side of it is
 * a stray drag — one that would blow the picture up to a smear. Kept in step
 * with Board::CROP_MIN_SIZE, which is where the stored window is held to it.
 */
export const MIN_CROP_SIZE = 0.05;

/** The window a board is drawn through, with "never cropped" read as all of it. */
export function cropOf(crop: BoardImageCrop | Record<string, never> | null | undefined): BoardImageCrop {
  if (!crop || typeof (crop as BoardImageCrop).width !== 'number') return FULL_IMAGE_CROP;

  return normalizeCrop(crop as BoardImageCrop);
}

/** A window held inside the picture, and no smaller than one worth having. */
export function normalizeCrop(crop: BoardImageCrop): BoardImageCrop {
  const x = clamp(crop.x, 0, 1 - MIN_CROP_SIZE);
  const y = clamp(crop.y, 0, 1 - MIN_CROP_SIZE);

  return {
    x,
    y,
    width: clamp(crop.width, MIN_CROP_SIZE, 1 - x),
    height: clamp(crop.height, MIN_CROP_SIZE, 1 - y),
  };
}

/** Whether two windows are the same one, give or take a pixel of a drag. */
export function sameCrop(a: BoardImageCrop, b: BoardImageCrop): boolean {
  const near = (p: number, q: number) => Math.abs(p - q) < 1e-6;

  return near(a.x, b.x) && near(a.y, b.y) && near(a.width, b.width) && near(a.height, b.height);
}

export function isFullCrop(crop: BoardImageCrop): boolean {
  return sameCrop(crop, FULL_IMAGE_CROP);
}

/**
 * A point of the original picture, read against the window cut out of it — which
 * is the picture everything outside the editor is drawn on.
 *
 * Outside `0..1` is a point the crop cut away. That is allowed and has to be:
 * a gym that frames the photo tighter than the wall leaves the grid's corners
 * off the edge of it, and a corner pinned back to the edge would describe a
 * different wall. See Board::CALIBRATION_RANGE, which is how far out one may sit.
 */
export function croppedPoint(point: CalibrationPoint, crop: BoardImageCrop): CalibrationPoint {
  return { x: (point.x - crop.x) / crop.width, y: (point.y - crop.y) / crop.height };
}

/** The reverse: where a point of the cropped picture falls in the original. */
export function uncroppedPoint(point: CalibrationPoint, crop: BoardImageCrop): CalibrationPoint {
  return { x: crop.x + point.x * crop.width, y: crop.y + point.y * crop.height };
}

/**
 * The four corners moved between the original picture and the window cut out of
 * it. The grid editor works on the whole upload — that is the only place a crop
 * can be widened again from, and the only place a corner the crop cut off is
 * still reachable — while the board is stored and drawn against the window.
 */
function mapCalibration(
  calibration: BoardCalibration | null,
  crop: BoardImageCrop,
  move: (point: CalibrationPoint, crop: BoardImageCrop) => CalibrationPoint
): BoardCalibration | null {
  if (!calibration || isFullCrop(crop)) return calibration;

  const corners = cornersOf(calibration);

  return {
    top_left: move(corners.top_left, crop),
    top_right: move(corners.top_right, crop),
    bottom_left: move(corners.bottom_left, crop),
    bottom_right: move(corners.bottom_right, crop),
  };
}

/** Corners of the original picture, as the cropped one stores and draws them. */
export function croppedCalibration(
  calibration: BoardCalibration | null,
  crop: BoardImageCrop
): BoardCalibration | null {
  return mapCalibration(calibration, crop, croppedPoint);
}

/** Corners of a cropped board, back on the upload the editor works on. */
export function uncroppedCalibration(
  calibration: BoardCalibration | null,
  crop: BoardImageCrop
): BoardCalibration | null {
  return mapCalibration(calibration, crop, uncroppedPoint);
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;

  return Math.min(max, Math.max(min, value));
}

/** Looks a climb's hold up in the layout, so it is drawn at its real size. */
export function holdSpecFor(
  holds: BoardHoldMap | null | undefined,
  hold: BoardHold
): BoardHoldSpec | null {
  return holds?.[cellKey(hold)] ?? null;
}
